import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface Reservation {
  id: string;
  org_id: string;
  battery_type_id: string;
  quantity: number;
  status: string;
  notes?: string;
  created_by?: string;
  created_at: string;
  updated_at: string;
  organizations?: {
    id: string;
    name: string;
  };
  battery_types?: {
    id: string;
    name: string;
    capacity_kwh: number;
    power_kw: number;
  };
}

export const useReservations = () => {
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchReservations = async () => {
    try {
      const { data, error } = await supabase
        .from('reservations')
        .select(`
          *,
          organizations:org_id(id, name),
          battery_types:battery_type_id(id, name, capacity_kwh, power_kw)
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setReservations(data || []);
    } catch (error) {
      console.error('Error fetching reservations:', error);
      toast({
        title: 'Fout bij ophalen reserveringen',
        description: 'Kon reserveringen niet laden',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReservations();

    const channel = supabase
      .channel('reservations-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'reservations' }, () => {
        fetchReservations();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  const createReservation = async (data: {
    org_id: string;
    battery_type_id: string;
    quantity: number;
    notes?: string;
  }) => {
    try {
      const { error } = await supabase
        .from('reservations')
        .insert([data]);

      if (error) throw error;

      toast({
        title: 'Reservering aangemaakt',
        description: 'De reservering is succesvol aangemaakt'
      });
      
      await fetchReservations();
      return true;
    } catch (error) {
      console.error('Error creating reservation:', error);
      toast({
        title: 'Fout bij aanmaken reservering',
        description: 'Kon reservering niet aanmaken',
        variant: 'destructive'
      });
      return false;
    }
  };

  const updateReservationStatus = async (id: string, status: string) => {
    try {
      const { error } = await supabase
        .from('reservations')
        .update({ status })
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Status bijgewerkt',
        description: 'Reservering status is bijgewerkt'
      });
      
      await fetchReservations();
      return true;
    } catch (error) {
      console.error('Error updating reservation:', error);
      toast({
        title: 'Fout bij bijwerken status',
        description: 'Kon status niet bijwerken',
        variant: 'destructive'
      });
      return false;
    }
  };

  const deleteReservation = async (id: string) => {
    try {
      const { error } = await supabase
        .from('reservations')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({
        title: 'Reservering verwijderd',
        description: 'De reservering is succesvol verwijderd'
      });
      
      await fetchReservations();
      return true;
    } catch (error) {
      console.error('Error deleting reservation:', error);
      toast({
        title: 'Fout bij verwijderen reservering',
        description: 'Kon reservering niet verwijderen',
        variant: 'destructive'
      });
      return false;
    }
  };

  return {
    reservations,
    loading,
    createReservation,
    updateReservationStatus,
    deleteReservation,
    refetch: fetchReservations
  };
};
